/**
 * SearchStoreCard Component
 *
 * Simplified store card for search results.
 * The entire card is clickable, linking to the store detail page.
 */

'use client';

import type { StoreHit } from '@/lib/algolia/stores';
import { Link } from '@/lib/i18n/navigation';
import { boutiquePaths } from '@/lib/paths';
import { useTranslations } from 'next-intl';
import Image from 'next/image';

import styles from './SearchStoreCard.module.scss';

export interface SearchStoreCardProps {
  /** Store data from Algolia */
  store: StoreHit;
  /** Locale for URL generation */
  locale?: string;
  /** Index for test IDs */
  index?: number;
}

/**
 * Search Store Card
 *
 * Simplified card for search results - entire card is a link.
 * Shows: image, name, address, phone.
 */
export function SearchStoreCard({ store, index }: SearchStoreCardProps) {
  const t = useTranslations('search.stores');

  // Get store image - prefer photo gallery, fall back to thumbnail
  const storeImage = store.photo_gallery?.[0]?.image?.url || store.thumbnail_url;

  // Get localized name/address (fall back to English versions)
  const name = store.name_en || store.name;
  const address = store.city ? `${store.street}, ${store.city}` : store.street;

  const storeUrl = boutiquePaths.detail(store.slug);

  return (
    <Link href={storeUrl} className={styles.card} data-testid={`search-store-card-${index}`}>
      {/* Store Image */}
      <div className={styles.imageWrapper}>
        {storeImage ? (
          <Image
            src={storeImage}
            alt={name}
            fill
            className={styles.image}
            sizes="(max-width: 768px) 100vw, 300px"
          />
        ) : (
          <div className={styles.imagePlaceholder} />
        )}
      </div>

      {/* Store Info */}
      <div className={styles.info}>
        <h3 className={styles.name}>{name}</h3>
        <p className={styles.address}>{address}</p>
        {store.phone && (
          <p className={styles.phone}>
            {t('phone')} {store.phone}
          </p>
        )}
      </div>
    </Link>
  );
}

SearchStoreCard.displayName = 'SearchStoreCard';
